import React, { useState, useEffect } from 'react';
import { io } from "socket.io-client";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";

import quizInstanceStatusContext from '../context/quizInstanceStatusContext';
import Status from '../enums/QuizInstanceStatus';

import QuizAuth from '../components/QuizAuth';
import QuizApi from '../QuizApi';
import { shuffle } from '../utils';
import { PENDING_ALIAS_KEY } from '../providers/CourseSessionProvider';

function quizInfoFromServerResponse(serverQuizInfo) 
{
  const quizInfo = Object.assign({}, serverQuizInfo, {

    endsAt: serverQuizInfo.endsAt ? new Date(serverQuizInfo.endsAt) : undefined,
    startedAt: serverQuizInfo.startedAt ? new Date(serverQuizInfo.startedAt) : undefined,

    questionList: serverQuizInfo.questionList 
      ? serverQuizInfo.questionList.sort((a, b) => a.order < b.order ? -1 : 1)
      : undefined

  });

  return quizInfo;
}

function tryAssignAnswerPositions(quizInfo)
{
  if (quizInfo.answerIndexMap)
  {
    return;
  }

  quizInfo.answerIndexMap = {};
  if (quizInfo.questionList)
  {
    for (const question of quizInfo.questionList)
    {
      if (question.answerList)
      {
        shuffle(question.answerList);

        for (let i = 0; i < question.answerList.length; i++)
        {
          const answer = question.answerList[i];
          quizInfo.answerIndexMap[answer.id] = i;
        }
      }
    }
  }
}

export default function QuizStatusProvider(props) {

  const { quizAccessShortId } = props;
  const [quizAnswerData, setQuizAnswerData] = useState(null);

  const [quizStatus, setQuizStatus] = useState(() => {

    // A course session push (see CourseSessionProvider) leaves the
    // participant's known alias here right before navigating here — when
    // present, skip straight past the join form and auto-submit it, exactly
    // as if the participant had typed it in themselves. This only reads the
    // value (never clears it) — React.StrictMode invokes state initializers
    // twice in development, and clearing it here would make the second call
    // see it already gone. Clearing happens once, separately, below.
    let pendingAlias = "";
    try {
      pendingAlias = sessionStorage.getItem(PENDING_ALIAS_KEY) || "";
    } catch (e) {
      // Ignore storage errors — falls back to the normal join form.
    }

    return {
      status: pendingAlias ? Status.AuthProvided : Status.Initial,

      authCount: -1,
      authParams: pendingAlias ? { alias: pendingAlias } : {},
      authType: "",

      errorMessage: "",

      quizParticipantId: "",
      quizAccessShortId: "",
      quizInfo: {},

      socket: null,

      // Child components should be able to set the answer map
      setQuizAnswerData
    };
  });
  const [joinQuizArgs] = useState({ quizAccessShortId });

  // Clears the handoff key exactly once. Removing an already-removed key is
  // a no-op, so this stays safe even though StrictMode runs effects twice.
  useEffect(() => {
    try {
      sessionStorage.removeItem(PENDING_ALIAS_KEY);
    } catch (e) {
      // Ignore storage errors
    }
  }, []);

  // ===============================
  // QUIZ AUTH LOOPING EFFECT
  // ===============================
  useEffect(() => { 
    const start = async () => {

      try {

        if (quizStatus.status === Status.Initial 
          || (
            quizStatus.status === Status.AuthProvided && 
            quizStatus.authParams && Object.keys(quizStatus.authParams).length > 0
         )
        )
        {
          if (!joinQuizArgs.quizAccessShortId) {
            throw new Error("Invalid quiz link");
          }

          setQuizStatus({ ...quizStatus, status: Status.Loading });

          const apiResponse = await QuizApi.quizJoin(joinQuizArgs.quizAccessShortId, quizStatus.authParams);
          if (!apiResponse || !apiResponse.success || !apiResponse.response) {
            throw new Error(apiResponse.errorMsg || "Invalid API response");
          }

          const joinResponse = apiResponse.response;
          if (joinResponse.auth && joinResponse.auth.required)
          {
            // Failed to join, re-display auth
            setQuizStatus({ 
              ...quizStatus, 

              status: Status.AuthRequired, 
              authType: joinResponse.auth.type,
              authErrorList: joinResponse.auth.errorList,
              defaultAlias: joinResponse.auth.defaultAlias,
              authCount: quizStatus.authCount + 1
            });
          }
          else 
          {
            const quizInfo = quizInfoFromServerResponse(joinResponse.quizInfo);
            tryAssignAnswerPositions(quizInfo);

            let newStatus = Status.Joined;

            if (quizInfo.completed)
            {
              newStatus = Status.Review;
            }
            else if (quizInfo.config?.standingMode && quizInfo.type !== "Free" && quizInfo.participantAnswerList)
            {
              // Standing instances never wait for "everyone" — a participant
              // who already answered goes straight to the live results view.
              // Exception: a standing Free-text instance accepts repeated
              // answers from the same participant, so revisiting the link
              // sends them back to the answer form (default Status.Joined)
              // instead of parking them on the results view.
              newStatus = Status.LiveResults;
            }
            else if (quizInfo.started && (quizInfo.type === "Quiz" || quizInfo.type === "Poll" || quizInfo.type === "Musicquiz") && quizInfo.participantAnswerList)
            {
              newStatus = Status.WaitingForCompletion;
            }

            // Successfully joined quiz
            setQuizStatus({
              ...quizStatus,

              status: newStatus,
              quizInfo: quizInfo,
              quizAccessId: joinResponse.quizAccessId,
              quizAccessShortId: joinResponse.quizAccessShortId,
              quizParticipantId: joinResponse.quizParticipantId,
              quizStyle: joinResponse.quizStyle,

              // Initialize Socket.IO
              socket: io("/", { 
                path: "/socketIO",
                query: {
                  pid: joinResponse.quizParticipantId
                }
              })

            });
          }
        }

      } catch (error) {
        console.error(error);
        setQuizStatus({
          ...quizStatus,
          status: Status.Error,
          errorMessage: error.message
        });
      }
    };
    start();

  }, [quizStatus, joinQuizArgs]);

  // ==================================== 
  // UPDATE QUIZ INFO FROM SERVER
  // ====================================
  useEffect(() => {

    const eventName = "quizInfoUpdated";
    const eventHandler = msg => 
    {
      const newQuizInfo = quizInfoFromServerResponse(msg.quizInfo);
      const mergedQuizInfo = Object.assign({}, quizStatus.quizInfo, newQuizInfo);
      tryAssignAnswerPositions(mergedQuizInfo);

      let newStatus = quizStatus.status;
      if (mergedQuizInfo.completed)
      {
        newStatus = Status.Review;
      }

      setQuizStatus({
        ...quizStatus,

        quizInfo: mergedQuizInfo,
        status: newStatus
      })
    };

    if (quizStatus.socket && !quizStatus.socket.hasListeners(eventName))
    {
      quizStatus.socket.on(eventName, eventHandler);
    }

    return () => 
    {
      if (quizStatus.socket)
      {
        quizStatus.socket.off(eventName, eventHandler);
      }
    };

  }, [quizStatus]);

  // ======================================
  // SEND ANSWERS TO BACKEND WHEN PROVIDED
  // ======================================
  useEffect(() => {
 
    const fn = async () => 
    {
      if (quizStatus.status === Status.Joined && quizAnswerData !== null)
      {
        setQuizStatus({...quizStatus, status: Status.Loading});

        const responseBody = await QuizApi.quizInstanceAnswerSave(quizStatus.quizParticipantId, quizAnswerData); 
        if (!responseBody || !responseBody.success) 
        {
          setQuizStatus({
            ...quizStatus, 
            status: Status.Error, 
            errorMessage: responseBody.errorMessage 
          });
        }
        else 
        {
          const newQuizInfo = quizInfoFromServerResponse(responseBody.response.quizInfo);
          let newStatus = quizStatus.status;

          if (newQuizInfo.completed)
          {
            newStatus = Status.Review;
          }
          else if (newQuizInfo.config?.standingMode)
          {
            // No "waiting for others" for a standing instance — go straight
            // to the live aggregate results, updated as more people answer.
            newStatus = Status.LiveResults;
          }
          else if (newQuizInfo.started && (newQuizInfo.type === "Quiz" || newQuizInfo.type === "Poll" || newQuizInfo.type === "Musicquiz"))
          {
            newStatus = Status.WaitingForCompletion;
          }

          const mergedQuizInfo = Object.assign({}, quizStatus.quizInfo, newQuizInfo);
          tryAssignAnswerPositions(mergedQuizInfo);

          setQuizAnswerData(null);
          setQuizStatus({
            ...quizStatus, 

            status: newStatus,
            quizInfo: mergedQuizInfo
          });
        }
      }
    }

    fn();

  }, [quizStatus, quizAnswerData]);

  const { children } = props;
  const showJoinQuizLoader = 
       quizStatus.status === Status.Initial 
    || quizStatus.status === Status.Loading;

  return (
    <quizInstanceStatusContext.Provider value={quizStatus}>

      {showJoinQuizLoader && (
        <div id="loaderScreen">
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" />
          </div>
        </div>
      )}

      {quizStatus.status === Status.Error && (
        <Alert variant="danger">
          <h4>Error: {quizStatus.errorMessage}</h4>
        </Alert>
      )}

      {quizStatus.status === Status.AuthRequired && (
        <QuizAuth 
          authType={quizStatus.authType}
          authCount={quizStatus.authCount}
          authErrorList={quizStatus.authErrorList}
          defaultAlias={quizStatus.defaultAlias}
          onAuthProvided={authParams => setQuizStatus({
            ...quizStatus,
            status: Status.AuthProvided,
            authParams: authParams
          })} 
        />
      )}

      {(quizStatus.status === Status.Joined || quizStatus.status === Status.Review || quizStatus.status === Status.LiveResults) && (
        <>
          {children}
        </>
      )}

      {quizStatus.status === Status.Ended && (
        <Alert variant="info">
          Quiz ended
        </Alert>
      )}

      {quizStatus.status === Status.WaitingForCompletion && (
        <div id="quizWaitingForCompletion">
          <div className="d-flex justify-content-center align-items-center h-100">
            <div className="text-center">
              <h3>Bra jobbat!</h3>
              {quizStatus.quizInfo.type === "Quiz" && <p>När alla är klara så kommer facit att visas.</p>}
            </div>
          </div>
        </div>
      )}

    </quizInstanceStatusContext.Provider>
  );
}

